import React, { FunctionComponent } from 'react'
import { StyleSheet } from 'react-native'

import { Image as ExpoImage } from 'expo-image'
import styled from 'styled-components/native'

import { ThemeType } from '../../utils/themes/ThemeProvider'
import { Image } from '../image/Image'

export type TreatmentRecViewport = 'DESKTOP' | 'TABLET' | 'MOBILE'

export type TreatmentRecImageSizes = {
  DESKTOP: { height: string; width: string }
  TABLET: { height: string; width: string }
  MOBILE: { height: string; width: string }
}

const ImageSizer = styled(Image)<{ height: string; width: string }>(({ height, width }) => ({
  width,
  height,
}))

const Container = styled.View({
  overflow: 'hidden',
})

const AuroraContainer = styled.View<{ theme: ThemeType }>(
  ({
    theme: {
      breakpoints: { isMinWidthTablet },
    },
  }) => ({
    overflow: 'hidden',
    ...(isMinWidthTablet && { flex: 1 }),
  }),
)

/**
 * Styled image component for Aurora treatment recommendation cards.
 * - On tablet/desktop: Uses object-fit: cover with bottom positioning to fill the container
 * - On mobile: Uses fixed dimensions from IMAGE_SIZES.MOBILE
 */
const AuroraImage = styled(ExpoImage)<{ theme: ThemeType; mobileSize: { width: string; height: string } }>(
  ({
    theme: {
      breakpoints: { isMinWidthTablet },
    },
    mobileSize,
  }) =>
    isMinWidthTablet
      ? {
          ...StyleSheet.absoluteFillObject,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'bottom',
        }
      : mobileSize,
)

export type TreatmentRecImageProps = {
  /** The viewport size to render */
  viewport?: TreatmentRecViewport
  /** Image URI for the current theme */
  uri: string
  /** Test ID for the image element (should be wrapped with tID by caller) */
  testID?: string
  /** Whether to use Aurora styling */
  isAuroraTheme: boolean
  /** Image sizes for each viewport (used for legacy rendering and Aurora mobile) */
  imageSizes: TreatmentRecImageSizes
}

/**
 * Shared image component for treatment recommendation cards.
 * Handles both Aurora and legacy rendering with proper sizing and object-fit behavior.
 */
export const TreatmentRecImage: FunctionComponent<TreatmentRecImageProps> = ({
  viewport = 'DESKTOP',
  uri,
  testID,
  isAuroraTheme,
  imageSizes,
}) => {
  if (isAuroraTheme) {
    return (
      <AuroraContainer>
        <AuroraImage testID={testID} source={{ uri }} mobileSize={imageSizes.MOBILE} accessibilityIgnoresInvertColors />
      </AuroraContainer>
    )
  }

  return (
    <Container>
      <ImageSizer {...imageSizes[viewport]} testID={testID} source={{ uri }} accessibilityIgnoresInvertColors />
    </Container>
  )
}
